import React, { useEffect, useState } from 'react';

import { toZonedTime } from 'date-fns-tz';

import { getStringFromDate } from '../../NylasCalendar/utils';

interface Props {
  date: string; // YYYY-MM-DD format
  startHour: number;
  endHour: number;
  timezone: Timezone;
  schedulingIncrement: number;
}

const MILLISECONDS_PER_MINUTE = 60000;

export const CurrentTimeIndicator: React.FC<Props> = ({ date, startHour, endHour, schedulingIncrement, timezone }) => {
  const [currentTime, setCurrentTime] = useState<Date | null>(null);

  useEffect(() => {
    const tick = () => {
      const now = new Date();

      const nowInTz = timezone?.name
        ? toZonedTime(now, timezone.name)
        : new Date(now.getTime() - (timezone?.offset ?? 0));

      const nowDateStr = getStringFromDate(nowInTz);
      const withinSameDay = nowDateStr === date;
      const h = nowInTz.getHours();
      const withinVisibleHours = h >= startHour && h < endHour;

      // Update state only when the indicator should be visible; otherwise clear it to avoid stale render.
      if (withinSameDay && withinVisibleHours) {
        setCurrentTime(now);
      } else {
        setCurrentTime(null);
      }
    };

    // Run immediately on mount
    tick();

    const interval = setInterval(tick, MILLISECONDS_PER_MINUTE);

    return () => clearInterval(interval);
  }, [date, startHour, endHour, timezone]);

  if (!currentTime) {
    return null;
  }

  // Shift the current time into the calendar timezone. We prefer using the TZ name when available,
  // but fall back to the pre-computed `offset` (difference between local and calendar TZ in ms).
  const timeInCalendarTz = timezone?.name
    ? toZonedTime(currentTime, timezone.name)
    : new Date(currentTime.getTime() - (timezone?.offset ?? 0));

  const currentHour = timeInCalendarTz.getHours();
  const currentMinutes = timeInCalendarTz.getMinutes();

  // Calculate position based on scheduling increment
  // Each time slot has a specific height based on the increment
  const rungHeight = schedulingIncrement === 15 ? 12 : 24; // 15min = 12px, 30min = 24px
  const slotsPerHour = 60 / schedulingIncrement; // 4 slots for 15min, 2 slots for 30min
  const hourHeight = rungHeight * slotsPerHour; // Total height per hour

  const hourOffset = currentHour - startHour;
  const minuteOffset = currentMinutes / 60;

  //  8 is the height of the line, so -4 to align it
  const topPosition = (hourOffset + minuteOffset) * hourHeight - 4;

  // At this point we know indicator should be visible (state logic), so no further date/hour checks needed.

  return (
    <div
      data-testid='current-time-indicator'
      className='pointer-events-none absolute left-0 right-0 z-20 flex items-center border-none'
      style={{ top: `${topPosition}px` }}
    >
      <div className='h-2 w-2 rounded-full border border-red-700 bg-red-600 shadow-lg' />
      <div className='h-0.5 flex-1 bg-red-600 shadow-lg' />
    </div>
  );
};
